import { removeItem, setItem } from '../utils/storage';

export enum RegionEnum {
  SE = 'se-sto',
  BR = 'sa-east-1',
  EU = 'eu-north-1',
}

export enum EnvironmentEnum {
  PROD = 'prod',
  DEV = 'dev',
  STAGE = 'stage',
}

type DefaultMapViewport = {
  center: { lat: number; lng: number };
  zoom: number;
};

export type RegionConfig = Record<RegionEnum.SE | RegionEnum.BR, ServerConfig>;
export type RegionConfigDev = Record<RegionEnum.EU | RegionEnum.BR, ServerConfig>;

export type Config = {
  [EnvironmentEnum.DEV]: RegionConfigDev;
  [EnvironmentEnum.STAGE]: RegionConfig;
  [EnvironmentEnum.PROD]: RegionConfig;
};

export type ServerConfig = {
  apiUrl: string;
  coreApiUrl: string;
  defaultMapViewport: DefaultMapViewport;
  openMobileAppUrl: string;
  ssoRedirectUrl: string;
  apiUrlCom?: string;
};

const servers: Config = {
  [EnvironmentEnum.DEV]: {
    [RegionEnum.EU]: {
      apiUrl: 'https://api.eu-north-1.dev.cosafe.com',
      coreApiUrl: 'https://api.eu-north-1.dev.cosafe.com',
      openMobileAppUrl: 'https://api.eu-north-1.dev.cosafe.com/open/',
      defaultMapViewport: {
        center: {
          lat: 54,
          lng: 15,
        },
        zoom: 3,
      },
      ssoRedirectUrl: 'https://app.dev.cosafe.com/desktop_se_sso.html',
    },
    [RegionEnum.BR]: {
      apiUrl: 'https://api.sa-east-1.stage.cosafe.com',
      coreApiUrl: 'https://api.sa-east-1.stage.cosafe.com',
      openMobileAppUrl: 'https://api.eu-north-1.dev.cosafe.com/open/',
      defaultMapViewport: {
        center: {
          lat: -14,
          lng: -55,
        },
        zoom: 4,
      },
      ssoRedirectUrl: 'https://app.stage.cosafe.com/desktop_se_sso.html',
    },
  },
  [EnvironmentEnum.STAGE]: {
    [RegionEnum.SE]: {
      apiUrl: 'https://api.se-sto.stage.cosafe.com',
      coreApiUrl: 'https://api.se-sto.stage.cosafe.com',
      openMobileAppUrl: 'https://api.se-sto.stage.cosafe.com/open/',
      defaultMapViewport: {
        center: {
          lat: 54,
          lng: 15,
        },
        zoom: 3,
      },
      ssoRedirectUrl: 'https://app.stage.cosafe.com/desktop_se_sso.html',
    },
    [RegionEnum.BR]: {
      apiUrl: 'https://api.sa-east-1.stage.cosafe.com',
      coreApiUrl: 'https://api.sa-east-1.stage.cosafe.com',
      openMobileAppUrl: 'https://api.se-sto.stage.cosafe.com/open/',
      defaultMapViewport: {
        center: {
          lat: -14,
          lng: -55,
        },
        zoom: 4,
      },
      ssoRedirectUrl: 'https://app.stage.cosafe.com/desktop_se_sso.html',
    },
  },
  [EnvironmentEnum.PROD]: {
    [RegionEnum.SE]: {
      apiUrl: 'https://api.cosafe.se',
      apiUrlCom: 'https://api.cosafe.com',
      coreApiUrl: 'https://api.se-sto.prod.cosafe.com',
      openMobileAppUrl: 'https://api.cosafe.com/open/',
      defaultMapViewport: {
        center: {
          lat: 54,
          lng: 15,
        },
        zoom: 3,
      },
      ssoRedirectUrl: 'https://app.cosafe.com/desktop_se_sso.html',
    },
    [RegionEnum.BR]: {
      apiUrl: 'https://api.sa-east-1.prod.cosafe.com',
      coreApiUrl: 'https://api.sa-east-1.prod.cosafe.com',
      openMobileAppUrl: 'https://api.cosafe.com/open/',
      defaultMapViewport: {
        center: {
          lat: -14,
          lng: -55,
        },
        zoom: 4,
      },
      ssoRedirectUrl: 'https://app.cosafe.com/desktop_se_sso.html',
    },
  },
};

export const DEFAULT_REGION: RegionEnum = RegionEnum.SE;
let currentRegion: RegionEnum = DEFAULT_REGION;

export const isValidRegion = (region: string): region is RegionEnum => {
  return Object.values(RegionEnum).includes(region as RegionEnum);
};

export const setRegion = (region: string) => {
  if (isValidRegion(region)) {
    setItem('selectedRegion', region);
    currentRegion = region;
  } else {
    removeItem('selectedRegion');
    console.error(`Invalid region: ${region}`);
  }
};

/**
 * Derives the region from a token's `issuingUrl`, which carries the issuing
 * cluster host (e.g. `api.sa-east-1.prod.cosafe.com`). The RegionEnum values
 * are exactly those host segments, so a substring match is enough and is
 * agnostic to the scheme-less form the legacy .NET token uses.
 */
export const regionFromIssuingUrl = (issuingUrl: string) => {
  return Object.values(RegionEnum).find(region => issuingUrl.includes(region)) ?? null;
};

/**
 * Resolves the region to apply on app init. A region-less but authenticated
 * session (e.g. a legacy cookie upgraded by cookieToJwtExchange) is recovered
 * rather than left region-less — running without a region leaves the core
 * client unconfigured and can drive a session-refresh loop. Recovery order:
 * a valid stored/param region, else the region the held token was issued by
 * (keeps non-SE users logged in after a wiped `selectedRegion`), else SE.
 * Unauthenticated visitors keep no region so the login/region-probe flow owns it.
 */
export const resolveAppRegion = ({
  regionParam,
  storedRegion,
  tokenIssuingUrl,
  hasAuthSession,
}: {
  regionParam: string | null;
  storedRegion: string | null;
  tokenIssuingUrl?: string | null;
  hasAuthSession: boolean;
}) => {
  if (regionParam && isValidRegion(regionParam)) {
    return regionParam;
  }
  if (storedRegion && isValidRegion(storedRegion)) {
    return storedRegion;
  }
  if (!hasAuthSession) {
    return null;
  }
  return (tokenIssuingUrl ? regionFromIssuingUrl(tokenIssuingUrl) : null) ?? DEFAULT_REGION;
};

// Region's server config
export const serverConfigs =
  servers[process.env.REACT_APP_ENV_NAME as keyof typeof servers] ?? Object.values(servers)[0];

export const getServerConfig = (overrideRegion?: RegionEnum) =>
  serverConfigs[(overrideRegion ?? currentRegion) as keyof typeof serverConfigs] ??
  Object.values(serverConfigs)[0];
